import React from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

// Quote Activity Chart - shows quote requests over time
export const QuoteActivityChart = ({ data }) => {
  // Show empty state if no data provided
  if (!data || data.length === 0) {
    return (
      <div className="h-[200px] w-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-slate-500">No quote activity yet</p>
          <p className="text-xs text-slate-600 mt-1">Quote requests will appear here as they come in</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <defs>
            <linearGradient id="quoteGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#14B8A6" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#64748B', fontSize: 11 }} 
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#64748B', fontSize: 11 }} 
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1E293B', 
              border: '1px solid #334155', 
              borderRadius: '8px',
              color: '#F1F5F9'
            }}
            labelStyle={{ color: '#94A3B8' }}
          />
          <Area 
            type="monotone" 
            dataKey="quotes" 
            stroke="#14B8A6" 
            strokeWidth={2}
            fill="url(#quoteGradient)" 
            name="Quote Requests"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Geographic Heat Map - shows where quote requests come from
export const QuoteHeatMap = ({ data }) => {
  // Use provided data, or empty array if no real data
  const mapData = data && data.length > 0 ? data : [];
  
  if (mapData.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-slate-500">No quote requests yet</p>
        <p className="text-xs text-slate-600 mt-1">When clients request quotes, their locations will appear here</p>
      </div>
    );
  }
  
  const maxCount = Math.max(...mapData.map(d => d.count));
  
  return (
    <div className="space-y-2">
      {mapData.map((location, idx) => {
        const percentage = (location.count / maxCount) * 100;
        return (
          <div key={idx} className="flex items-center gap-3">
            <div className="w-28 text-sm text-slate-400 truncate">
              {location.city}, {location.state}
            </div>
            <div className="flex-1 h-6 bg-slate-800 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${percentage}%`,
                  background: `linear-gradient(90deg, #14B8A6 0%, #06B6D4 100%)`
                }}
              />
            </div>
            <div className="w-8 text-right text-sm font-mono text-slate-300">
              {location.count}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Profile Views Chart
export const ProfileViewsChart = ({ data }) => {
  // Show empty state if no data provided
  if (!data || data.length === 0) {
    return (
      <div className="h-[150px] w-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-slate-500">No profile views yet</p>
          <p className="text-xs text-slate-600 mt-1">Views will appear here once your profile is listed</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-[150px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
          <XAxis 
            dataKey="date" 
            tick={{ fill: '#64748B', fontSize: 10 }} 
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
          />
          <YAxis 
            tick={{ fill: '#64748B', fontSize: 10 }} 
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: '#1E293B', 
              border: '1px solid #334155', 
              borderRadius: '8px',
              color: '#F1F5F9'
            }}
          />
          <Bar 
            dataKey="views" 
            fill="#8B5CF6" 
            radius={[4, 4, 0, 0]}
            name="Profile Views"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default { QuoteActivityChart, QuoteHeatMap, ProfileViewsChart };
